import type {
  AlertSubscriptionDto,
  AlertSubscriptionListDto,
  CreateAlertBody,
  UpdateAlertBody,
} from '../models/alert.dto'
import type { TriggeredAlertDto } from '../models/history.types'

import { ALERT_HISTORY_OCCURRENCE_LIMIT } from '@/shared/config'
import { ApiError, appHttp, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

type AlertHttp = Pick<typeof appHttp, 'get' | 'post' | 'put' | 'delete'>

const FEATURE_OWNED_ERRORS = {
  suppressGlobalErrorHandler: true,
  meta: { suppressGlobalErrorHandler: true },
} as const

export interface AlertNotificationTypeDto {
  id: number
  name: string
}

/** The live registry nests the array one level deeper than either the migration contract
 *  (`{status, data: [...]}`) or the measured legacy shape (a bare array) promised — the real body
 *  is `{status, data: { configs: [...], results: [...] (duplicate), count }}`. */
interface AlertNotificationConfigsBody {
  configs: AlertNotificationTypeDto[]
}

/** Thin notification-subscription transport. Mapping and fault tolerance live in later tickets. */
export class AlertApi {
  constructor(private readonly http: AlertHttp = appHttp) {}

  /** Resolve the stable product identity to this environment's numeric transport id. */
  async resolveNotificationTypeId(name: string): Promise<number> {
    const response = await this.http.get<
      | ApiEnvelope<AlertNotificationTypeDto[] | AlertNotificationConfigsBody>
      | AlertNotificationTypeDto[]
    >('/notifications/subscription/configs/', FEATURE_OWNED_ERRORS)
    // Three shapes seen in the wild: a bare array (measured legacy producer), `{status, data:
    // [...]}` (the migration contract), and `{status, data: {configs: [...], ...}}` (the actual
    // live registry). `unwrapEnvelope` also raises on `status: 'error'`, so route anything that
    // isn't already a bare array through it before picking the array out.
    const body = response.data
    const unwrapped = Array.isArray(body) ? body : unwrapEnvelope({ data: body })
    const configs = Array.isArray(unwrapped)
      ? unwrapped
      : Array.isArray(unwrapped.configs)
        ? unwrapped.configs
        : []
    const match = configs.find((config) => config.name === name)
    if (!match || !Number.isInteger(match.id)) {
      throw new ApiError({
        kind: 'unknown',
        status: null,
        code: 'ALERT_NOTIFICATION_TYPE_UNAVAILABLE',
        fields: {},
        payload: configs,
        userVisible: true,
        detail: `Notification type ${name} is unavailable`,
      })
    }
    return match.id
  }

  async list(workspaceId: number | string, typeId: number): Promise<AlertSubscriptionDto[]> {
    const response = await this.http.get<ApiEnvelope<AlertSubscriptionListDto>>(
      `/notifications/subscription/workspace/${workspaceId}/${typeId}/`,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response).subscriptions
  }

  async create(
    workspaceId: number | string,
    typeId: number,
    body: CreateAlertBody,
    idempotencyKey: string,
  ): Promise<AlertSubscriptionDto> {
    const response = await this.http.post<ApiEnvelope<AlertSubscriptionDto>>(
      `/notifications/subscription/${workspaceId}/${typeId}/`,
      body,
      {
        ...FEATURE_OWNED_ERRORS,
        headers: { 'Idempotency-Key': idempotencyKey },
      },
    )
    return unwrapEnvelope(response)
  }

  async update(id: number, body: UpdateAlertBody): Promise<AlertSubscriptionDto> {
    const response = await this.http.put<ApiEnvelope<AlertSubscriptionDto>>(
      `/notifications/subscription/${id}/update/`,
      body,
      FEATURE_OWNED_ERRORS,
    )
    return unwrapEnvelope(response)
  }

  async setActive(id: number, active: boolean): Promise<void> {
    const action = active ? 'activate' : 'deactivate'
    const response = await this.http.put<ApiEnvelope<unknown>>(
      `/notifications/subscription/${id}/${action}/`,
      undefined,
      FEATURE_OWNED_ERRORS,
    )
    unwrapEnvelope(response)
  }

  async remove(id: number): Promise<void> {
    const response = await this.http.delete<ApiEnvelope<unknown>>(
      `/notifications/subscription/${id}/delete/`,
      FEATURE_OWNED_ERRORS,
    )
    unwrapEnvelope(response)
  }

  async getTriggered(
    workspaceId: number | string,
    occurrenceLimit = ALERT_HISTORY_OCCURRENCE_LIMIT,
  ): Promise<TriggeredAlertDto[]> {
    const response = await this.http.get<ApiEnvelope<TriggeredAlertDto[]>>(
      `/triggered-alerts/workspace/${workspaceId}/`,
      {
        ...FEATURE_OWNED_ERRORS,
        params: { occ_limit: occurrenceLimit },
      },
    )
    return unwrapEnvelope(response)
  }
}

export const alertApi = new AlertApi()
